import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, filter, fromEvent, tap } from 'rxjs';
import {
  WebViewMessage,
  WebViewTokenPayload,
} from '../../model/interface/webview.interface';
import { WEBVIEW_STORAGE_KEY } from '../../consts/common';
import jwt_decode from 'jwt-decode';
import { Decode } from '../../model/interface/auth';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class WebViewService {
  private isHandlerInitialized = false;

  constructor(private router: Router) {}

  private _isWebView$ = new BehaviorSubject<boolean>(
    localStorage.getItem(WEBVIEW_STORAGE_KEY) === 'true'
  );

  private _token$ = new BehaviorSubject<string | null>(null);

  public get isWebView$(): BehaviorSubject<boolean> {
    return this._isWebView$;
  }

  public get token$(): BehaviorSubject<string | null> {
    return this._token$;
  }

  private setIsWebView(value: boolean): void {
    this._isWebView$.next(value);
    if (value) {
      localStorage.setItem(WEBVIEW_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(WEBVIEW_STORAGE_KEY);
    }
  }

  public setToken(payload: WebViewTokenPayload): void {
    this._token$.next(payload.token);
    localStorage.setItem('token', payload.token);
    localStorage.setItem('refreshToken', payload.refreshToken || '');

    const decoded = jwt_decode(payload.token) as Decode;
    localStorage.setItem('LANGUAGE', decoded.language);
    localStorage.setItem('userId', decoded.user_id);
    localStorage.setItem('email', decoded.username);
    localStorage.setItem('phoneNumber', decoded.phoneNumber);
    localStorage.setItem('role', decoded.roles[0]);

    this.readyToNavigate();
  }

  public initWebViewHandler(): void {
    if (this.isHandlerInitialized) {
      return;
    }
    this.isHandlerInitialized = true;

    fromEvent<MessageEvent>(window, 'message')
      .pipe(
        filter((event: MessageEvent) => {
          const data = event.data as WebViewMessage;
          return !!data && !!data?.method;
        }),
        tap((event: MessageEvent) =>
          this.dispatchMessage(event.data as WebViewMessage)
        )
      )
      .subscribe();
  }

  private dispatchMessage(message: WebViewMessage): void {
    try {
      const key = message.method;
      if (!key) {
        console.warn('[WebView] Message without type/method:', message);
        return;
      }
      const handler = this.handlers[key];
      if (handler) {
        handler(message);
      }
    } catch (error) {
      console.error('[WebView] dispatchMessage error:', { message, error });
    }
  }

  private invokeFlutter<T>(method: string, props?: T): boolean {
    try {
      window.parent.postMessage({ method, props }, '*');
      return true;
    } catch (error) {
      console.error('[WebView] invokeFlutter error:', { method, props, error });
      return false;
    }
  }

  private readonly handlers: Record<string, (msg: WebViewMessage) => void> = {
    isWebView: (msg: WebViewMessage) => {
      this.setIsWebView(!!msg.payload);
    },
    setToken: (msg: WebViewMessage) => {
      this.handleTokenPayload(msg.payload as WebViewTokenPayload);
    },
    navigate: (msg: WebViewMessage) => {
      this.navigateToPath(msg.payload);
    },
  };

  public sendFileToMobile(file: File): void {
    if (!file) {
      console.warn('[WebView] sendFileToMobile called without file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      if (!result) {
        console.error('[WebView] FileReader result is empty');
        return;
      }

      const base64Data = result.includes(',') ? result.split(',')[1] : result;
      this.invokeFlutter('downloadFile', {
        name: file.name,
        type: file.type || 'application/octet-stream',
        data: base64Data,
      });
    };
    reader.onerror = error => {
      console.error('[WebView] FileReader error:', error);
    };
    reader.readAsDataURL(file);
  }

  public closeWebView(): void {
    this.invokeFlutter('onPopWebView');
  }

  public onLogOut(isSessionExpired: boolean): void {
    this.invokeFlutter<{ isSessionExpired: boolean }>('onLogOut', {
      isSessionExpired,
    });
  }

  public readyForWork(): void {
    this.invokeFlutter('readyForWork');
  }

  public readyToNavigate(): void {
    this.invokeFlutter('readyToNavigate');
  }

  private navigateToPath(payload: unknown): void {
    if (typeof payload !== 'string' || !payload.trim()) {
      console.warn('[WebView] navigate payload is invalid:', payload);
      return;
    }

    const normalizedPath = payload.startsWith('/') ? payload : `/${payload}`;
    this.router
      .navigateByUrl(normalizedPath, { replaceUrl: true })
      .catch(error =>
        console.error('[WebView] navigate error:', { payload, error })
      );
  }

  private handleTokenPayload(payload: WebViewTokenPayload): void {
    if (!payload || typeof payload !== 'object' || !payload.token) {
      console.warn('[WebView] setToken payload invalid:', payload);
      return;
    }

    this.setToken(payload);
  }
}
